import { computed } from 'vue';

import useAuth from '@/composables/useAuth';

type UseDataSharingPermissionsOptions = {
  /**
   * When true, only requires 'manage:sharing' permission.
   * When false or undefined, requires both 'manage:sharing' and 'manage:populations'.
   */
  isPartnerContext?: boolean;
};

/**
 * Composable for managing data sharing settings permissions.
 *
 * @param options - Configuration options
 * @param options.isPartnerContext - When editing from partner context, only requires manage:sharing.
 *                                    When editing populations directly, requires both permissions.
 * @returns Object containing canEdit computed property
 *
 * @example
 * // For population editing (requires both permissions)
 * const { canEdit } = useDataSharingPermissions();
 *
 * @example
 * // For partner context editing (only requires manage:sharing)
 * const { canEdit } = useDataSharingPermissions({ isPartnerContext: true });
 */
export default function useDataSharingPermissions(
  options: UseDataSharingPermissionsOptions = {},
) {
  const { hasPermission } = useAuth();
  const { isPartnerContext = false } = options;

  const canEdit = computed(() => {
    if (isPartnerContext) {
      return hasPermission('manage:sharing');
    }
    return (
      hasPermission('manage:sharing') && hasPermission('manage:populations')
    );
  });

  return {
    canEdit,
  };
}
